import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Download, Printer, Search, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';

import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';

const Statement = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const [filters, setFilters] = useState({
    accountType: 'CUSTOMER',
    accountId: '',
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    accountName: '' // Store name for title
  });
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Searchable account dropdown state
  const [accountSearch, setAccountSearch] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountDropdownRef = useRef(null);
  const accountInputRef = useRef(null);

  // Fetch accounts based on type
  const { data: accounts } = useQuery(
    ['accounts', filters.accountType, selectedBranch?.id],
    async () => {
      let endpoint = '';
      switch (filters.accountType) {
        case 'CUSTOMER':
          endpoint = '/api/customers';
          break;
        case 'SUPPLIER':
          endpoint = '/api/suppliers';
          break;
        case 'CASH_BANK':
          return [
            { id: 'CASH', name: 'Cash Account' },
            { id: 'BANK', name: 'Bank Account' },
            { id: 'UPI', name: 'UPI Account' }
          ];
        case 'EMPLOYEE':
          endpoint = '/api/users';
          break;
        case 'TECHNICIAN':
          endpoint = '/api/technicians';
          break;
        default:
          return [];
      }
      
      const response = await api.get(endpoint, {
        params: { branchId: selectedBranch?.id }
      });
      
      // Handle different response structures
      const data = response.data.data;
      if (Array.isArray(data)) {
        return data.map(item => ({
          ...item,
          name: item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.username || 'Unnamed Account'
        }));
      } else if (data && Array.isArray(data.customers)) {
        return data.customers.map(item => ({
          ...item,
          name: item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.username || 'Unnamed Account'
        }));
      } else if (data && Array.isArray(data.suppliers)) {
        return data.suppliers.map(item => ({
          ...item,
          name: item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.username || 'Unnamed Account'
        }));
      } else if (data && typeof data === 'object') {
        const key = Object.keys(data).find(k => Array.isArray(data[k]));
        if (key) {
          return data[key].map(item => ({
            ...item,
            name: item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.username || 'Unnamed Account'
          }));
        }
      }
      return [];
    },
    {
      enabled: !!selectedBranch && !!filters.accountType,
    }
  );

  // Fetch ledger entries
  const { data: ledgerData, isLoading } = useQuery(
    ['ledger-statement', selectedBranch?.id, filters],
    async () => {
      const response = await api.get('/api/ledger/statement', {
        params: {
          branchId: selectedBranch?.id,
          accountType: filters.accountType,
          accountId: filters.accountId,
          startDate: filters.startDate,
          endDate: filters.endDate
        }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch && !!filters.accountId,
    }
  );

  const entries = ledgerData?.entries || [];
  const openingBalance = ledgerData?.openingBalance || 0;
  const closingBalance = ledgerData?.closingBalance || 0;
  const totalDebit = ledgerData?.totalDebit || 0;
  const totalCredit = ledgerData?.totalCredit || 0;

  const handleExportExcel = () => {
    if (!filters.accountId || entries.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Get account name
    const selectedAccount = (accounts || []).find(acc => acc.id === filters.accountId);
    const accountName = selectedAccount?.name || filters.accountId;

    // Create professional Excel format with proper spacing and alignment
    const rows = [];
    
    // Title row
    rows.push(['STATEMENT / LEDGER REPORT']);
    rows.push([]);
    
    // Header information
    rows.push(['Branch:', selectedBranch?.name || '']);
    rows.push(['Account Type:', filters.accountType]);
    rows.push(['Account Name:', accountName]);
    rows.push(['Period:', `${format(new Date(filters.startDate), 'dd MMM yyyy')} to ${format(new Date(filters.endDate), 'dd MMM yyyy')}`]);
    rows.push(['Generated:', format(new Date(), 'dd MMM yyyy HH:mm:ss')]);
    rows.push([]);
    
    // Opening Balance
    rows.push(['Opening Balance:', '', '', '', Math.abs(openingBalance).toFixed(2)]);
    rows.push([]);
    
    // Table headers
    if (filters.accountType === 'TECHNICIAN') {
      rows.push(['Date', 'Job Number', 'Customer', 'Device', 'Job Status', 'Job Cost', 'Labour (Credit)', 'Payments (Debit)', 'Balance']);
    } else {
      rows.push(['Date', 'Narration', 'Debit', 'Credit', 'Balance']);
    }
    
    // Data rows
    entries.forEach(entry => {
      if (filters.accountType === 'TECHNICIAN') {
        const d = entry.jobDetails;
        rows.push([
          format(new Date(entry.transactionDate), 'dd/MM/yyyy'),
          d?.jobNumber || entry.jobNumber || 'N/A',
          d?.customerName || '',
          d?.device || '',
          d?.status || '',
          d?.actualCost > 0 ? d.actualCost.toFixed(2) : d?.estimatedCost > 0 ? d.estimatedCost.toFixed(2) : '0.00',
          entry.creditAmount > 0 ? entry.creditAmount.toFixed(2) : '',
          entry.debitAmount > 0 ? entry.debitAmount.toFixed(2) : '',
          Math.abs(entry.runningBalance).toFixed(2)
        ]);
      } else {
        rows.push([
          format(new Date(entry.transactionDate), 'dd/MM/yyyy'),
          entry.narration + (entry.referenceNumber ? ` (Ref: ${entry.referenceNumber})` : ''),
          entry.debitAmount > 0 ? entry.debitAmount.toFixed(2) : '',
          entry.creditAmount > 0 ? entry.creditAmount.toFixed(2) : '',
          Math.abs(entry.runningBalance).toFixed(2)
        ]);
      }
    });
    
    // Summary rows
    rows.push([]);
    if (filters.accountType === 'TECHNICIAN') {
      rows.push(['Total Debits (Payouts):', '', '', '', '', '', '', totalDebit.toFixed(2), '']);
      rows.push(['Total Credits (Labour Earnings):', '', '', '', '', '', totalCredit.toFixed(2), '', '']);
      rows.push(['Closing Net Balance:', '', '', '', '', '', '', '', Math.abs(closingBalance).toFixed(2)]);
    } else {
      rows.push(['Total Debit:', '', totalDebit.toFixed(2), '', '']);
      rows.push(['Total Credit:', '', '', totalCredit.toFixed(2), '']);
      rows.push(['Closing Balance:', '', '', '', Math.abs(closingBalance).toFixed(2)]);
    }
    
    // Convert to CSV with proper escaping
    const csvContent = rows.map(row => 
      row.map(cell => {
        // Escape cells containing commas or quotes
        const cellStr = String(cell || '');
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    ).join('\n');
    
    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = `Statement_${filters.accountType}_${accountName.replace(/[^a-z0-9]/gi, '_')}_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Statement exported successfully!');
  };

  const handleExportPDF = () => {
    if (!filters.accountId || entries.length === 0) {
      toast.error('No data to export');
      return;
    }

    const selectedAccount = (accounts || []).find(acc => acc.id === filters.accountId);
    const accountName = selectedAccount?.name || filters.accountId;

    const element = document.querySelector('.print-container');
    if (!element) return;

    const runDownload = () => {
      const opt = {
        margin:       10,
        filename:     `Statement_${filters.accountType}_${accountName.replace(/[^a-z0-9]/gi, '_')}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, allowTaint: true, logging: false, scrollX: 0, scrollY: 0 },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // 1. Save state and temporarily modify styles inline
      const prevStyle = element.getAttribute('style') || '';
      element.style.cssText = `
        visibility: visible !important;
        position: relative !important;
        left: auto !important;
        top: auto !important;
        width: 190mm !important;
        background: white !important;
        color: black !important;
        display: block !important;
        margin: 0 auto !important;
        z-index: 9999 !important;
        padding: 20px !important;
        box-shadow: none !important;
        border: none !important;
      `;

      // 2. Hide "no-print" sections in the original element
      const noPrintElements = element.querySelectorAll('.no-print');
      const prevNoPrintStyles = [];
      noPrintElements.forEach((el, idx) => {
        prevNoPrintStyles[idx] = el.getAttribute('style') || '';
        el.style.setProperty('display', 'none', 'important');
      });

      // 3. Make the hidden print header visible
      const printHeader = element.querySelector('.print-header-pdf');
      let prevPrintHeaderStyle = '';
      if (printHeader) {
        prevPrintHeaderStyle = printHeader.getAttribute('style') || '';
        printHeader.style.setProperty('display', 'block', 'important');
      }

      // 4. Wait for any images to load
      const allImages = Array.from(element.querySelectorAll('img'));
      const imagePromises = allImages.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(res => {
          img.onload = res;
          img.onerror = res;
        });
      });

      toast.loading('Generating Statement PDF...', { id: 'pdf-toast' });

      Promise.all(imagePromises).then(() => {
        requestAnimationFrame(() => {
          window.html2pdf().from(element).set(opt).save()
            .then(() => {
              // Restore states
              element.setAttribute('style', prevStyle);
              noPrintElements.forEach((el, idx) => {
                el.setAttribute('style', prevNoPrintStyles[idx]);
              });
              if (printHeader) {
                printHeader.setAttribute('style', prevPrintHeaderStyle);
              }
              toast.dismiss('pdf-toast');
              toast.success('Statement PDF downloaded successfully!');
            })
            .catch((err) => {
              console.error(err);
              element.setAttribute('style', prevStyle);
              noPrintElements.forEach((el, idx) => {
                el.setAttribute('style', prevNoPrintStyles[idx]);
              });
              if (printHeader) {
                printHeader.setAttribute('style', prevPrintHeaderStyle);
              }
              toast.dismiss('pdf-toast');
              toast.error('Failed to generate PDF.');
            });
        });
      });
    };

    if (!window.html2pdf) {
      toast.loading('Loading PDF library...', { id: 'pdf-toast' });
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = () => {
        toast.dismiss('pdf-toast');
        runDownload();
      };
      script.onerror = () => {
        toast.dismiss('pdf-toast');
        toast.error('Failed to load PDF library.');
      };
      document.head.appendChild(script);
    } else {
      runDownload();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 bg-[#FFF8DC]/60 p-4 sm:p-6 rounded-2xl border border-amber-200/40 shadow-sm print-container">
      {/* Print Styles */}
      <style>{`
        @media print {
          /* Hide sidebar, navigation, and everything else */
          nav, sidebar, header, .sidebar-container, .navbar-container, footer {
            display: none !important;
          }
          .no-print {
            display: none !important;
          }
          .print-container {
            background: white !important;
            border: none !important;
            padding: 0 !important;
            margin: 15mm 0 0 0 !important;
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }
          th, td {
            border: 1px solid #e5e7eb !important;
            color: black !important;
            padding: 8px !important;
          }
        }
      `}</style>

      {/* Print Header - Only visible when printing */}
      {filters.accountId && (
        <div className="hidden print:block print-header-pdf mb-6 border-b pb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {filters.accountName} - {filters.accountType} Statement
          </h2>
          <p className="text-sm text-gray-650">Branch: {selectedBranch?.name}</p>
          <p className="text-sm text-gray-650">Period: {format(new Date(filters.startDate), 'dd MMM yyyy')} to {format(new Date(filters.endDate), 'dd MMM yyyy')}</p>
          <p className="text-sm text-gray-650">Generated: {new Date().toLocaleString()}</p>
        </div>
      )}

      {/* Responsive Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
            {filters.accountId && filters.accountName 
              ? `${filters.accountName} - ${filters.accountType} Statement` 
              : 'Statement / Ledger'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">{selectedBranch?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 sm:flex-initial bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg inline-flex items-center justify-center transition-colors text-sm"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </button>
          <div className="relative flex-1 sm:flex-initial">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center justify-center transition-colors text-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
            {showExportDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-250 text-left">
                <button
                  onClick={() => {
                    handleExportExcel();
                    setShowExportDropdown(false);
                  }}
                  className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-semibold text-left"
                >
                  Export as Excel (.csv)
                </button>
                <button
                  onClick={() => {
                    handleExportPDF();
                    setShowExportDropdown(false);
                  }}
                  className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-semibold text-left"
                >
                  Export as PDF (.pdf)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 no-print">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type
            </label>
            <select
              value={filters.accountType}
              onChange={(e) => setFilters({ ...filters, accountType: e.target.value, accountId: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="CUSTOMER">Customer</option>
              <option value="SUPPLIER">Supplier</option>
              <option value="CASH_BANK">Cash/Bank</option>
              <option value="TECHNICIAN">Technician</option>
              <option value="EMPLOYEE">Employee (Users)</option>
            </select>
          </div>

          {/* Account Selection - Searchable Dropdown */}
          <div className="relative" ref={accountDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Account
            </label>
            <div className="relative">
              <input
                ref={accountInputRef}
                type="text"
                value={filters.accountId ? filters.accountName : accountSearch}
                onChange={(e) => {
                  setAccountSearch(e.target.value);
                  setShowAccountDropdown(true);
                  if (filters.accountId) {
                    setFilters({ ...filters, accountId: '', accountName: '' });
                  }
                }}
                onFocus={() => {
                  setShowAccountDropdown(true);
                  if (!filters.accountId) {
                    setAccountSearch('');
                  }
                }}
                onBlur={() => setTimeout(() => setShowAccountDropdown(false), 200)}
                placeholder="Type to search or select..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
              />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            
            {showAccountDropdown && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {!filters.accountId && !accountSearch && (
                  <div
                    onMouseDown={() => {
                      setShowAccountDropdown(false);
                    }}
                    className="px-4 py-2.5 text-sm text-gray-400 italic border-b border-gray-100"
                  >
                    Type to search accounts...
                  </div>
                )}
                {(accounts || [])
                  .filter(account => {
                    if (!accountSearch) return true;
                    const search = accountSearch.toLowerCase();
                    return (account.name || '').toLowerCase().includes(search) || 
                           (account.phone || '').includes(search);
                  })
                  .map((account) => (
                    <div
                      key={account.id}
                      onMouseDown={() => {
                        setFilters({ ...filters, accountId: account.id, accountName: account.name || '' });
                        setAccountSearch('');
                        setShowAccountDropdown(false);
                      }}
                      className={`px-4 py-2.5 hover:bg-orange-50 cursor-pointer text-sm border-b border-gray-50 last:border-b-0 ${
                        filters.accountId === account.id ? 'bg-orange-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-800">{account.name}</span>
                        {account.phone && (
                          <span className="text-xs text-gray-500 ml-2">{account.phone}</span>
                        )}
                      </div>
                      {account.email && (
                        <div className="text-xs text-gray-400 mt-0.5">{account.email}</div>
                      )}
                    </div>
                  ))}
                {(accounts || []).filter(account => {
                  if (!accountSearch) return true;
                  const search = accountSearch.toLowerCase();
                  return (account.name || '').toLowerCase().includes(search) || 
                         (account.phone || '').includes(search);
                }).length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    No accounts found matching "{accountSearch}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Opening Balance */}
      {filters.accountId && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-650">Opening Balance:</span>
            <span className={`text-lg font-bold ${openingBalance > 0 ? 'text-red-600' : openingBalance < 0 ? 'text-green-600' : 'text-gray-900'}`}>
              {currencySymbol}{Math.abs(openingBalance).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Statement Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      ) : filters.accountId ? (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                {filters.accountType === 'TECHNICIAN' ? (
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      DATE
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      JOB NO / CUSTOMER
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      DEVICE & PROBLEM
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      STATUS
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      JOB COST
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      LABOUR (CREDIT)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      PAYMENTS (DEBIT)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      BALANCE
                    </th>
                  </tr>
                ) : (
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      DATE
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      NARRATION
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      DEBIT
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      CREDIT
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      BALANCE
                    </th>
                  </tr>
                )}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={filters.accountType === 'TECHNICIAN' ? 8 : 5} className="px-6 py-12 text-center text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-lg font-medium">No transactions found</p>
                      <p className="text-sm mt-1">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  entries.map((entry, index) => {
                    if (filters.accountType === 'TECHNICIAN') {
                      const details = entry.jobDetails;
                      const statusColors = {
                        PENDING: 'bg-red-100 text-red-800',
                        IN_PROGRESS: 'bg-orange-100 text-orange-800',
                        COMPLETED: 'bg-green-100 text-green-800',
                        DELIVERED: 'bg-purple-100 text-purple-800',
                        RETURNED: 'bg-gray-100 text-gray-800'
                      };

                      return (
                        <tr key={entry.id} className="hover:bg-blue-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {format(new Date(entry.transactionDate), 'dd/MM/yyyy')}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            <div className="font-bold text-blue-600">
                              {details?.jobNumber || entry.jobNumber || 'N/A'}
                            </div>
                            {details?.customerName && (
                              <div className="text-xs text-gray-700 mt-0.5">
                                Cust: <span className="font-semibold">{details.customerName}</span>
                              </div>
                            )}
                            {details?.customerPhone && (
                              <div className="text-[10px] text-gray-500">{details.customerPhone}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px]">
                            {details?.device ? (
                              <div className="font-medium truncate" title={details.device}>
                                {details.device}
                              </div>
                            ) : (
                              <div className="font-medium text-gray-500 italic">No job linked</div>
                            )}
                            {details?.problemDescription ? (
                              <div className="text-xs text-gray-500 truncate" title={details.problemDescription}>
                                {details.problemDescription}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 italic truncate">{entry.narration}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                            {details?.status ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${statusColors[details.status] || 'bg-gray-100'}`}>
                                {details.status.replace('_', ' ')}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                            {details?.actualCost > 0 
                              ? `${currencySymbol}${details.actualCost.toLocaleString()}` 
                              : details?.estimatedCost > 0 
                                ? `${currencySymbol}${details.estimatedCost.toLocaleString()}` 
                                : `${currencySymbol}0`}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-green-700 text-right font-bold">
                            {entry.creditAmount > 0 ? `${currencySymbol}${entry.creditAmount.toLocaleString()}` : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                            {entry.debitAmount > 0 ? `${currencySymbol}${entry.debitAmount.toLocaleString()}` : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-right">
                            <span className={entry.runningBalance > 0 ? 'text-red-600' : entry.runningBalance < 0 ? 'text-green-600' : 'text-gray-900'}>
                              {currencySymbol}{Math.abs(entry.runningBalance).toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={entry.id} className="hover:bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {format(new Date(entry.transactionDate), 'dd/MM/yyyy')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">{entry.narration}</div>
                          {entry.referenceNumber && (
                            <div className="text-xs text-gray-500">Ref: {entry.referenceNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {entry.debitAmount > 0 ? `${currencySymbol}${entry.debitAmount.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {entry.creditAmount > 0 ? `${currencySymbol}${entry.creditAmount.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                          <span className={entry.runningBalance > 0 ? 'text-red-600' : entry.runningBalance < 0 ? 'text-green-600' : 'text-gray-900'}>
                            {currencySymbol}{Math.abs(entry.runningBalance).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          {entries.length > 0 && (
            <div className="bg-gray-50 p-4 sm:p-6 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="text-center bg-white p-3 rounded-lg shadow-sm border border-gray-150">
                  <p className="text-[10px] sm:text-xs text-gray-550 font-bold uppercase tracking-wider">
                    {filters.accountType === 'TECHNICIAN' ? 'Total Payouts (Debited)' : 'Total Amount'}
                  </p>
                  <p className="text-base sm:text-lg font-black text-gray-900 mt-1">{currencySymbol}{totalDebit.toLocaleString()}</p>
                </div>
                <div className="text-center bg-white p-3 rounded-lg shadow-sm border border-gray-150">
                  <p className="text-[10px] sm:text-xs text-gray-550 font-bold uppercase tracking-wider">
                    {filters.accountType === 'TECHNICIAN' ? 'Total Earnings (Credited)' : 'Already Paid'}
                  </p>
                  <p className="text-base sm:text-lg font-black text-gray-900 mt-1">{currencySymbol}{totalCredit.toLocaleString()}</p>
                </div>
                <div className="text-center bg-white p-3 rounded-lg shadow-sm border border-gray-150">
                  <p className="text-[10px] sm:text-xs text-gray-550 font-bold uppercase tracking-wider">
                    {filters.accountType === 'TECHNICIAN' ? 'Net Balance' : 'Balance Due'}
                  </p>
                  <p className={`text-base sm:text-lg font-black mt-1 ${closingBalance > 0 ? 'text-red-600' : closingBalance < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                    {currencySymbol}{Math.abs(closingBalance).toLocaleString()}
                    {filters.accountType === 'TECHNICIAN' && (
                      <span className="text-[10px] sm:text-xs font-semibold ml-1 block sm:inline">
                        {closingBalance < 0 ? '(Owed by Shop)' : closingBalance > 0 ? '(Overpaid)' : ''}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Search className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Account</h3>
          <p className="text-gray-600">Choose an account type and account to view the statement</p>
        </div>
      )}
    </div>
  );
};

export default Statement;


